import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CreditFacilityService } from './credit-facility.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

@Component({
  selector: 'gtw-delete-credit-facility',
  templateUrl: './delete-credit-facility.component.html',
  styleUrls: ['./delete-credit-facility.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DeleteCreditFacilityComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Component state
  rowData: any = {};
  colData: any = {};
  gridData: any = {};
  filterParams: any = {};
  globalService: any = {};
  globalParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  // Row data properties
  logged_in_user: string = '';

  modalConfig: ConfirmationModalConfig = {
    title: 'Please Confirm',
    message: 'Are you sure you want to delete this Credit Facility?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };

  // Data inputs from AngularJS
  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    if (!baseUrls) return;
    const urls = safeJsonInput<any>(baseUrls, null, 'delete-credit-facility.base-urls');
    if (urls && urls.base_url) {
      this.GENERAL_CONFIG.base_url = urls.base_url;
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    const data = safeJsonInput<any>(headerData, null, 'delete-credit-facility.header-data');
    if (data && Object.keys(data).length > 0) {
      this.globalService = data.globalParams || {};
      this.globalParams = data.globalParams || {};
      this.GENERAL_CONFIG = data.generalConfigData || {};
      this.USER_SETTINGS = data.userSettingsData || {};
      this.logged_in_user = this.USER_SETTINGS.user?.sso_id || '';
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    let data = safeJsonInput<any>(rowData, null, 'delete-credit-facility.row-data');
    if (data === null) return;
    // Handle double-encoded payloads: "\"{...}\""
    if (typeof data === 'string') {
      data = safeJsonInput<any>(data, data, 'delete-credit-facility.row-data(double-encoded)');
    }
    // Normalize grid payload shapes like:
    // 1) [{ row_id: "1", data: {...actual row...} }]
    // 2) [{ ...actual row... }]
    if (Array.isArray(data) && data.length > 0) {
      this.rowData = (data[0] && data[0].data) ? data[0].data : (data[0] || {});
    } else if (data && data.data && typeof data.data === 'object') {
      this.rowData = data.data;
    } else {
      this.rowData = data || {};
    }
    this.init();
  }

  @Input('col-data')
  set _col_data(colData: string) {
    this.colData = safeJsonInput<any>(colData, {}, 'delete-credit-facility.col-data') || {};
  }

  @Input('grid-data')
  set _grid_data(gridData: string) {
    this.gridData = safeJsonInput<any>(gridData, {}, 'delete-credit-facility.grid-data') || {};
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    const params = safeJsonInput<any>(filterParams, null, 'delete-credit-facility.filter-params');
    if (params && Object.keys(params).length > 0) {
      this.filterParams = params;
      this.init();
    }
  }

  constructor(
    private creditFacilityService: CreditFacilityService,
    private _snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.init();
  }

  init() {
    try {
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const hasRowData = this.rowData && Object.keys(this.rowData).length > 0;

      if (hasFilterParams && hasRowData) {
        console.log('DeleteCreditFacility init - rowData:', this.rowData);
        console.log('DeleteCreditFacility init - globalParams:', this.globalParams);
      }
    } catch (e) {
      console.error('Error in DeleteCreditFacility init:', e);
    }
  }

  onConfirmDelete() {
    this.deleteCreditFacility();
  }

  deleteCreditFacility() {
    this.modalConfig = { ...this.modalConfig, loading: true };

    const clobData = this.gridData.map((item: any) => {
      const row = item.data || item;
      return {
        trans_details_key: row.trans_details_key || row.TRANS_DETAILS_KEY || '',
        line_attrib_key: row.line_attrib_key || row.LINE_ATTRIB_KEY || '',
        occurrence: row.occurrence || row.OCCURRENCE || '',
        attrib_value: row.attrib_value || row.ATTRIB_VALUE || '',
        trans_type_key: row.trans_type_key || row.TRANS_TYPE_KEY || ''
      };
    });

    const deletePayload = clobData[0] || {};

    const clobSettings = [{
      sso_id: this.logged_in_user,
      screen_key: this.globalParams.screen_key || this.filterParams.screen_key
    }];

    console.log('globalParams for delete:', this.globalParams);
    console.log('filterParams for delete:', this.filterParams);

    this.creditFacilityService.deleteData(
      this.GENERAL_CONFIG.base_url,
      clobData,
      clobSettings,
      'DELETE_CREDIT_FACILITY',
      this.logged_in_user,
      this.globalParams,
      this.filterParams,
      deletePayload
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Credit Facility deleted successfully.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        console.error('Error deleting Credit Facility:', error);
        this._snackBar.open('Error occurred while deleting. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
